import React, { Component } from 'react';
import { useParams, useNavigate } from "react-router-dom";
import { HeaderModern, TopBar, TopHeader, Footer, Loader } from '../layouts/general';
import { useAuth } from '../../contexts/AuthContext';
import { API_BASE, getImageUrl } from 'config/api';
import { BRAND_BLUE } from 'theme';
import SEO, { buildAbsoluteUrl, truncateDescription } from '../seo/SEO';

const defaultImage = (typeof process.env.PUBLIC_URL === 'string' ? process.env.PUBLIC_URL : '') + '/images/logo-blog.png';

const normalizeFeature = (item) => {
    if (typeof item === 'string') {
        return { title: item, description: '' };
    }
    if (item && typeof item === 'object') {
        return {
            title: item.title || '',
            description: item.description || ''
        };
    }
    return { title: String(item), description: '' };
};

const buildServiceHeroStats = (service, featureCount) => {
    const items = [
        { label: 'Key Capabilities', value: featureCount },
        { label: 'Methodologies', value: (service.methodologies || []).length },
        { label: 'Deliverables', value: (service.deliverables || []).length },
        { label: 'Industries Served', value: (service.industries || []).length }
    ].filter((item) => item.value > 0);

    return items.length > 0 ? items : [{ label: 'Key Capabilities', value: featureCount || 1 }];
};

const ServiceDetailWrapper = (props) => {
    const params = useParams();
    const { isAuthenticated } = useAuth();
    const navigate = useNavigate();
    return <ServiceDetail {...props} params={params} isAuthenticated={isAuthenticated} navigate={navigate} />;
};

class ServiceDetail extends Component {
    constructor(props) {
        super(props);
        this.state = {
            headers: [{ id: 1, names: 'Service Details' }],
            service: null,
            loading: true
        };
    }

    async componentDidMount() {
        await this.fetchService();
    }

    async componentDidUpdate(prevProps) {
        if (prevProps.params?.slug !== this.props.params?.slug) {
            this.setState({ loading: true, service: null });
            await this.fetchService();
        }
    }

    fetchService = async () => {
        const { slug } = this.props.params || {};
        if (!slug) {
            this.setState({ loading: false });
            return;
        }
        try {
            const res = await fetch(`${API_BASE}/services/${slug}`);
            const data = await res.json();
            this.setState({ service: data || null, loading: false });
        } catch (e) {
            this.setState({ service: null, loading: false });
        }
    };

    handleRequestService = () => {
        const { isAuthenticated, navigate } = this.props;
        const { service } = this.state;
        const destination = `/consultation?service=${encodeURIComponent(service?.title || '')}`;
        if (isAuthenticated) {
            navigate(destination);
        } else {
            navigate(`/user-login?redirect=${encodeURIComponent(destination)}`);
        }
    };

    renderContent(service) {
        const imageUrl = getImageUrl(service.image);
        const tagline = service.description || '';
        const overview = service.overview && service.overview !== tagline ? service.overview : '';
        const features = (Array.isArray(service.keyFeatures) ? service.keyFeatures : [])
            .map(normalizeFeature)
            .filter((item) => item.title || item.description);

        const heroText = tagline || overview;
        const stats = buildServiceHeroStats(service, features.length);

        return (
            <>
                <section className="modern-service-hero">
                    <div className="container">
                        <div className="modern-service-hero-content">
                            <h1>{service.title}</h1>
                            {heroText && <p>{heroText}</p>}
                            <button type="button" className="modern-service-hero-cta" onClick={this.handleRequestService}>
                                Request Service
                            </button>
                        </div>
                    </div>

                    <div className="modern-service-hero-stats">
                        <div className="container">
                            <div className="modern-service-hero-stats-grid">
                                {stats.map((item) => (
                                    <div className="modern-service-hero-stat" key={item.label}>
                                        <span>{item.label}</span>
                                        <strong>{item.value}+</strong>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                <section className="modern-service-detail-page">
                    <div className="container">
                        <div className="modern-service-detail-grid">
                            <aside className="modern-service-detail-media-col">
                                <div className={`modern-service-detail-media${imageUrl ? '' : ' is-fallback'}`}>
                                    {imageUrl ? (
                                        <img
                                            src={imageUrl}
                                            alt={service.title}
                                            onError={(e) => {
                                                e.target.src = defaultImage;
                                                e.target.parentElement.classList.add('is-fallback');
                                            }}
                                        />
                                    ) : (
                                        <span className="modern-service-icon" style={{ color: service.iconColor || BRAND_BLUE }}>
                                            <i className={`fa ${service.icon || 'fa-bar-chart'}`} aria-hidden="true" />
                                        </span>
                                    )}
                                </div>
                            </aside>

                            <div className="modern-service-detail-main">
                                {overview && <p className="modern-service-detail-summary">{overview}</p>}

                                {features.length > 0 && (
                                    <div className="modern-service-detail-features">
                                        {features.map((item, index) => (
                                            <div className="modern-service-detail-feature" key={`${item.title}-${index}`}>
                                                {item.title && <strong>{item.title}</strong>}
                                                {item.description && <span>{item.description}</span>}
                                            </div>
                                        ))}
                                    </div>
                                )}

                            </div>
                        </div>
                    </div>
                </section>
            </>
        );
    }

    render() {
        const { service, loading } = this.state;

        if (loading) {
            return (
                <div className="bg-body3">
                    <div className="boxed">
                        <Loader />
                        <TopBar />
                        <TopHeader />
                        <section className="modern-service-detail-page">
                            <div className="container">
                                <p className="modern-service-detail-status">Loading…</p>
                            </div>
                        </section>
                        <Footer />
                    </div>
                </div>
            );
        }

        if (!service) {
            return (
                <div className="bg-body3">
                    <div className="boxed">
                        <TopBar />
                        <TopHeader />
                        <section className="modern-service-detail-page">
                            <div className="container">
                                <div className="modern-service-detail-main">
                                    <h1>Service Not Found</h1>
                                </div>
                            </div>
                        </section>
                        <Footer />
                    </div>
                </div>
            );
        }

        return (
            <div className="bg-body3">
                <div className="boxed">
                    <SEO
                        title={service.metaTitle || `${service.title} | Stats Elite Services`}
                        description={service.metaDescription || service.description || service.overview || `${service.title} consulting and training from Stats Elite.`}
                        path={`/services/${service.slug || this.props.params?.slug || ''}`}
                        image={getImageUrl(service.image) || undefined}
                        schema={{
                            '@context': 'https://schema.org',
                            '@type': 'Service',
                            name: service.title,
                            description: truncateDescription(service.description || service.overview || ''),
                            provider: { '@type': 'Organization', name: 'Stats Elite', url: buildAbsoluteUrl('/') },
                            areaServed: ['Tanzania', 'East Africa'],
                            url: buildAbsoluteUrl(`/services/${service.slug || this.props.params?.slug || ''}`)
                        }}
                    />
                    <Loader />
                    <TopBar />
                    <TopHeader />
                    {
                        this.state.headers.map(data => (
                            <HeaderModern data={{ ...data, names: service.title }} key={data.id} />
                        ))
                    }

                    {this.renderContent(service)}

                    <Footer />
                </div>
            </div>
        );
    }
}

export default ServiceDetailWrapper;
